import { useState } from 'react';
import { Button } from 'primereact/button';
import { Link } from 'react-router-dom';
import { Message } from 'primereact/message';
import { useLogin } from '@mezzodevelopment/mezzo-ui/auth';
import { FormInputText } from '../components/form';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import mezzoLogoSrc from '../assets/images/mezzo-full.png';

const darkLogo = process.env.PUBLIC_URL + '/Logo-Centro_dark.png';
const mezzoLogo = mezzoLogoSrc;

const products = [
  {
    name: 'Credit',
    icon: 'pi-credit-card',
    color: '#2571ab',
    desc: 'One integration, all credit order types and all providers.',
  },
  {
    name: 'Underwrite',
    icon: 'pi-shield',
    color: '#0a1f3d',
    desc: 'Dual AUS with modernized response for rep and warrant relief.',
  },
  {
    name: 'Price',
    icon: 'pi-tag',
    color: '#6366f1',
    desc: 'Data enrichment for flexible pricing agility with any PPE vendor.',
  },
  {
    name: 'Fees',
    icon: 'pi-dollar',
    color: '#2571ab',
    desc: 'Multi-vendor support with centralized lender fee configuration.',
  },
  {
    name: 'VoX',
    icon: 'pi-bolt',
    color: '#76b729',
    desc: 'Income, employment, and assets — any vendor, any verification type.',
  },
  {
    name: 'Centro',
    icon: 'pi-th-large',
    color: '#0a1f3d',
    desc: 'Enterprise configuration, dashboard analytics, and emulators.',
  },
];

const Login = (): JSX.Element => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { handleLogin, isPasswordRequired, isLoading } = useLogin();

  const validationSchema = yup.object({
    username: yup.string().required('Please enter your username').trim(),
    password: yup.string().when([], {
      is: () => isPasswordRequired,
      then: (schema) => schema.required('Please enter your password').trim(),
      otherwise: (schema) => schema.trim(),
    }),
  });

  const {
    control,
    formState: { errors: formErrors },
    handleSubmit,
  } = useForm({
    defaultValues: {
      username: '',
      password: '',
    },
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (data: { username: string; password?: string }) => {
    try {
      setErrorMessage(null);
      if (isPasswordRequired) {
        await (handleLogin as any)({ username: data.username, password: data.password });
      } else {
        await (handleLogin as any)({ username: data.username });
      }
    } catch (error) {
      setErrorMessage('Invalid credentials. Please try again.');
    }
  };

  return (
    <div className="login-container">
      {/* Left panel — Mezzo products showcase */}
      <div className="login-hero">
        <div className="login-hero-inner">
          <div className="login-hero-top">
            <img src={mezzoLogo} alt="Mezzo" className="login-mezzo-logo" />
            <p className="login-hero-tagline">Reimagine your mortgage tech stack</p>
          </div>

          <div className="login-products-grid">
            {products.map((p) => (
              <div key={p.name} className="login-product-card">
                <div className="login-product-icon" style={{ background: p.color }}>
                  <i className={`pi ${p.icon}`} />
                </div>
                <div className="login-product-info">
                  <div className="login-product-name">{p.name}</div>
                  <div className="login-product-desc">{p.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="login-hero-footer">
            <a href="https://www.teammezzo.com/products/" target="_blank" rel="noreferrer" className="login-learn-more">
              Learn more at teammezzo.com <i className="pi pi-external-link" style={{ fontSize: '0.7rem' }} />
            </a>
            <span className="login-copyright">&copy; {new Date().getFullYear()} Mezzo Technology, Inc.</span>
          </div>
        </div>
      </div>

      {/* Right panel — Centro login form */}
      <div className="login-form-panel">
        <div className="login-card">
          <div className="login-logo-area">
            <Link to="/">
              <img src={darkLogo} alt="Centro" className="login-logo" />
            </Link>
            <span className="login-powered-by">Powered by Mezzo</span>
          </div>

          <div className="login-welcome">
            <h2 className="login-welcome-title">Welcome back</h2>
            <p className="login-welcome-sub">Sign in to your Centro dashboard</p>
          </div>

          {errorMessage && (
            <div className="mb-3">
              <Message severity="error" className="w-full" text={errorMessage} />
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(onSubmit)();
            }}
          >
            <div className="grow p-fluid p-input-filled">
              <FormInputText
                key={'username'}
                control={control}
                errors={formErrors}
                field={'username'}
                label={'Username'}
                disabled={isLoading}
              />

              {isPasswordRequired && (
                <FormInputText
                  key={'password'}
                  control={control}
                  errors={formErrors}
                  field={'password'}
                  label={'Password'}
                  disabled={isLoading}
                  type="password"
                  autoFocus={true}
                />
              )}
              <Button
                label={isLoading ? 'Authenticating...' : 'Sign In'}
                icon={isLoading ? 'pi pi-spin pi-spinner' : 'pi pi-arrow-right'}
                iconPos="right"
                loading={isLoading}
                className="login-submit-btn"
                type="submit"
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
